import ApiService, { type RequestOptions } from '@/services/api.service'
import type { AxiosResponse } from 'axios'
import type {
  BenchmarkPerformanceRequest,
  ClientGNAVRequest,
  ClientGNAVResponse,
  ComparePeriodsRequest,
  ComparePeriodsResponse,
  PerformanceRequest,
  PerformanceResponse,
  EntityGNAVRequest,
  EntityGNAVResponse,
  PerformanceStatsRequest,
  PerformanceStatsResponse,
  TagBenchmarkPerformanceRequest,
  TagCompositionTimeseriesRequest,
  TagCompositionTimeseriesResponse,
  AssetsPerformanceRequest,
  AssetsPerformanceResponse,
  PearsonCorrelationResponse,
  PnlAttributionData,
  PnlAttributionRequest,
} from '@/types/services/performance'

class PerformanceService {
  static getComparePeriodsData(
    params: ComparePeriodsRequest
  ): Promise<AxiosResponse<ComparePeriodsResponse>> {
    return ApiService.post(
      '/api/v1/root/search/get_compare_periods_data',
      {
        aggr: {
          params,
        },
      },
      { allowConcurrentRequests: true }
    )
  }

  static getPerformance(
    params: PerformanceRequest
  ): Promise<AxiosResponse<PerformanceResponse>> {
    const url = params.root_user_id
      ? 'get_client_performance'
      : 'get_fund_performance'
    return ApiService.post(`/api/v1/root/search/${url}`, {
      aggr: {
        params,
      },
    })
  }

  static calculatePerformanceStats(
    returns: PerformanceStatsRequest,
    allowConcurrentRequests = false
  ): Promise<AxiosResponse<PerformanceStatsResponse>> {
    return ApiService.post(
      '/api/v1/root/search/calculate_performance_stats',
      {
        aggr: {
          params: {
            returns,
          },
        },
      },
      { allowConcurrentRequests }
    )
  }

  static getBenchmarkPerformance(
    params: BenchmarkPerformanceRequest,
    allowConcurrentRequests = false
  ): Promise<AxiosResponse<PerformanceResponse>> {
    return ApiService.post(
      '/api/v1/root/search/get_benchmark_performance',
      {
        aggr: {
          params,
        },
      },
      { allowConcurrentRequests }
    )
  }

  static getPearsonCorrelation(
    fund_returns: PerformanceStatsRequest,
    benchmark_returns: PerformanceStatsRequest,
    allowConcurrentRequests = false
  ): Promise<AxiosResponse<PearsonCorrelationResponse>> {
    return ApiService.post(
      '/api/v1/root/search/calculate_pearson_correlation',
      {
        aggr: {
          params: {
            returns1: fund_returns,
            returns2: benchmark_returns,
          },
        },
      },
      { allowConcurrentRequests }
    )
  }

  static cancelCalculatePerformanceStats() {
    ApiService.cancelRequests(
      '/api/v1/root/search/calculate_performance_stats',
      'POST'
    )
  }

  static cancelGetBenchmarkPerformance() {
    ApiService.cancelRequests(
      '/api/v1/root/search/get_benchmark_performance',
      'POST'
    )
  }

  static cancelGetPearsonCorrelation() {
    ApiService.cancelRequests(
      '/api/v1/root/search/calculate_pearson_correlation',
      'POST'
    )
  }

  static getTagBenchmarkPerformance(
    params: TagBenchmarkPerformanceRequest,
    allowConcurrentRequests = false
  ): Promise<AxiosResponse<PerformanceResponse>> {
    return ApiService.post(
      '/api/v1/root/search/get_tag_benchmark_performance',
      {
        aggr: {
          params,
        },
      },
      { allowConcurrentRequests }
    )
  }

  static getTagCompositionTimeseries(
    params: TagCompositionTimeseriesRequest
  ): Promise<AxiosResponse<TagCompositionTimeseriesResponse>> {
    return ApiService.post(
      '/api/v1/root/search/get_tag_composition_timeseries',
      {
        aggr: {
          params,
        },
      }
    )
  }

  static getAssetsPerformance(
    params: AssetsPerformanceRequest
  ): Promise<AxiosResponse<AssetsPerformanceResponse>> {
    return ApiService.post('/api/v1/accounts/held_assets_performance', params)
  }

  static getPnlAttribution(
    params: PnlAttributionRequest
  ): Promise<AxiosResponse<PnlAttributionData>> {
    return ApiService.post('/api/v3/accounts/assets/pnl_attribution', params)
  }

  /**
   * Get GNAV of given entity
   **/
  static getEntityGNAV(
    params: EntityGNAVRequest
  ): Promise<AxiosResponse<EntityGNAVResponse>> {
    return ApiService.post('/api/v1/root/search/get_entity_gnav', {
      aggr: {
        params,
      },
    })
  }

  static getClientGNAV(
    params: ClientGNAVRequest,
    options?: RequestOptions
  ): Promise<AxiosResponse<ClientGNAVResponse>> {
    return ApiService.post(
      '/api/v1/root/search/get_client_gnav',
      { aggr: { params } },
      options
    )
  }
}

export default PerformanceService
